import { useRef, useState } from "react";
import type { ReactElement } from "react";
import type { ApiClient, SupplierContract, Unternehmen } from "@kundeninfo/api-client";
import { Hinweis, Knopf, Pillenreihe } from "@kundeninfo/ui/me-design";
import { UnternehmensChips, sichtFuer } from "../../components/Filterkopf.js";
import { sharedAssignmentLists } from "../../components/KontierungFelder.js";
import { useStabileListe } from "../../components/stabileListe.js";
import type { SupplierContractsClient } from "./KundeLiefervertraege.js";
import { LiefervertragObjekt, useContractActions } from "./LiefervertragObjekt.js";
import type { TicketWeg } from "./LiefervertragObjekt.js";
import { toggleState, useAreaQuery } from "./shared.js";
import { SUPPLIER_TEXT_WEB as T } from "./texts.js";
import "./Suppliers.css";

/**
 * Der BEREICH „Lieferverträge“ (Spec „Bereiche“; seit „Öffnen statt Springen“ Aufgabe 3 als Liste von
 * `LiefervertragObjekt`, K12): Unternehmens-Chips (R21, das aktive immer dabei), Status-Chips „laufend“/„gekündigt“/
 * „beendet“ (mehrfach, keiner gewählt = alle, in der Adresse, A-W3) und die Objekte in der Ordnung des Servers (Frist in der
 * Vorwarnzeit zuerst). Derselbe Bau wie in der Akte, dazu der Lieferant im Bezug und der Sprungknopf in die Akte (Quelle
 * `search`, Herkunft samt Status-Chips — `sprungZiel`). Bearbeiten, Kondition, Kündigung und Ticket an Ort und Stelle
 * (`useContractActions`); danach lädt die Liste im Stehen nach und hält das Objekt an seiner Stelle. Die Leiste wechselt
 * ihre Gestalt durch keine Wahl; ein Filterwechsel hält sie an ihrer Stelle (`useAreaQuery`). Ein Ladefehler ist kein
 * leerer Bereich.
 */
type State = keyof typeof T.chips;
const STATES: readonly State[] = ["RUNNING", "TERMINATED", "ENDED"];

export function BereichLiefervertraege(p: {
  client: SupplierContractsClient & Pick<ApiClient, "listSupplierContractsArea">; unternehmen: Unternehmen[];
  aktivesUnternehmen: string | null;
  /** Die Status-Chips aus der Adresse (leer = alle) und der Weg, sie zu ändern (A-W3). */
  states: readonly State[]; onStates: (states: State[]) => void;
  /** Der Ticketweg: ohne ihn kein „Ticket anlegen“ im „⋯“. */
  ticket?: TicketWeg;
}): ReactElement {
  const [extra, setExtra] = useState<string[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [lists] = useState(() => sharedAssignmentLists(p.client));
  const liste = useRef<HTMLDivElement | null>(null);
  const companyIds = useStabileListe(sichtFuer(p.aktivesUnternehmen, extra));
  const states = p.states;
  const query = { ...(companyIds === undefined ? {} : { companyIds }), ...(states.length === 0 ? {} : { states: [...states] }) };
  const { data, error, toolbar, change, reload, stamp } =
    useAreaQuery(query, (q) => p.client.listSupplierContractsArea(q), T.loadFailed);
  const nachladen = (id: string): void =>
    reload([...(liste.current?.children ?? [])].find((e) => e.getAttribute("data-contract-id") === id) ?? null);
  const aktionen = useContractActions({ client: p.client, lists, companies: p.unternehmen,
    ...(p.ticket === undefined ? {} : { ticket: p.ticket }), onDone: nachladen });
  const items: SupplierContract[] | null = data?.items ?? null;

  return (
    <div className="sup sup--area">
      <div ref={toolbar} className="sup__toolbar">
        <UnternehmensChips label={T.companiesLabel} companies={p.unternehmen} aktivId={p.aktivesUnternehmen} zusatz={extra}
          onZusatz={(ids) => change(() => setExtra(ids))} />
        <div className="sup__chips" role="group" aria-label={T.statesLabel}>
          <Pillenreihe>
            <Knopf art="sekundaer" gestalt="pille" gedrueckt={states.length === 0} onClick={() => change(() => p.onStates([]))}>
              {T.allStates}
            </Knopf>
            {STATES.map((s) => (
              <Knopf key={s} art="sekundaer" gestalt="pille" gedrueckt={states.includes(s)}
                onClick={() => change(() => p.onStates(toggleState(STATES, states, s)))}>
                {T.chips[s]}
              </Knopf>
            ))}
          </Pillenreihe>
        </div>
      </div>

      {error !== null && <Hinweis art="fehler">{error}</Hinweis>}
      {items === null && error === null && <p className="sup__muted" role="status">{T.loading}</p>}
      {items !== null && items.length === 0 && error === null && <p className="sup__muted">{T.empty}</p>}
      {items !== null && items.length > 0 && (
        // W-4: alle Objekte direkte Geschwister EINES Behälters.
        <div ref={liste} className="sup__objekte">
          {items.map((c) => (
            <LiefervertragObjekt key={c.id} vertrag={c} ort="einkauf" aufgeklappt={open === c.id}
              onAufgeklappt={(offen) => setOpen(offen ? c.id : null)} client={p.client} lists={lists}
              companies={p.unternehmen} mehrereUnternehmen={companyIds !== undefined} refresh={stamp}
              mitTicket={aktionen.mitTicket} gesperrt={aktionen.offen} onAktion={(a) => aktionen.start(a, c)}
              onGeaendert={() => nachladen(c.id)} />
          ))}
        </div>
      )}
      {aktionen.panels}
    </div>
  );
}
